import { cn } from "@/lib/helpers/cn";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Heading2 } from "@/ui2/atoms/Heading";
import type { PatientDetails } from "@/core/types";

import { calculateAge } from "../../helpers/calculate-age";

type PatientDetailsProps = {
  isSticky?: boolean;
  patientDetails: Pick<
    PatientDetails,
    "dob" | "first_name" | "gender" | "last_name" | "preferred_name" | "preferred_pronouns"
  >;
};

export function PatientDetails({ isSticky, patientDetails }: PatientDetailsProps) {
  const { dob, first_name, gender, last_name, preferred_name, preferred_pronouns } = patientDetails;
  const patientName = `${first_name} ${last_name}`;
  const age = calculateAge(dob);
  const [dobMm, dobDd, dobYyyy] = dob.split("/");
  const dobIso = `${dobYyyy}-${dobMm}-${dobDd}`;

  return (
    <div
      className={cn("tw:flex tw:flex-col", isSticky ? "tw:justify-center" : "tw:justify-start")}
      {...getAutomationDataAttr("member-profile-header-details")}
    >
      <hgroup className="tw:flex tw:gap-2.5">
        <Heading2 as="h1" data-private>
          {patientName}
          {preferred_name && <span className="tw:ml-ch">({preferred_name})</span>}
        </Heading2>

        {preferred_pronouns && <p className="body-md">{preferred_pronouns}</p>}
      </hgroup>

      <dl className="body-sm tw:flex tw:gap-ch">
        <div>
          <dt className="tw:sr-only">Date of birth</dt>
          <dd>
            <time dateTime={dobIso}>{dob}</time> ({age})
          </dd>
        </div>

        {gender && (
          <div className="tw:flex tw:items-baseline tw:gap-ch tw:before:content-['•']">
            <dt className="tw:sr-only">Gender</dt>
            <dd className="tw:capitalize">{gender}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}
